"use client";

import React, { useState } from "react";
import { hooks, apijs } from "@lib/client";
import Consts from "../../../../config/consts.js";
import logjs from "@metrojs/logjs";

// import { providers } from "@lib/client";

import QaabDialog from "./QaabDialog.js";
import { object } from "prop-types";

// const { useWebAppContext } = providers;

const api = new apijs("api/QaabDialog");
const log = new logjs("useQaabDialog");

/**
 * Q&A from AB ダイアログ用のカスタムフック
 * Q&A from AB画面の状態管理とAPI呼び出しを処理
 * @param {Object} initState - フォームの初期状態
 * @param {Object} state - 追加の状態
 * @param {Object} actions - アクション関数
 * @returns {Array} [formData, formProps] - フォームデータとプロパティ
 */
export const useQaabDialog = (initState = {}, state = {}, actions = {}) => {
  const { open, onClose, isOpen, dialogProps, renderDialog } = hooks.useCustomDialog(QaabDialog, { resetOnClose: true });

  const [form, formProps] = hooks.useFormEx(initState, state, actions);

  // 状態管理
  const [requestData, setRequestData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [qaList, setQaList] = useState([]);
  const [qaCnt, setQaCnt] = useState(0);
  const [mikaitoList, setMikaitoList] = useState([]);

  const [isShowInit, setIsShowInit] = useState(true);
  const [isShowMikaitoList, setIsShowMikaitoList] = useState(true);
  const [isAnswerOpen, setIsAnswerOpen] = useState(false);
  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isShowFinish, setIsShowFinish] = useState(false);

  const [selectedQa, setSelectedQa] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Q&A from AB データの取得
   * @param {string} mitsumoriNo - 見積番号
   * @param {string} raid - 見積回答ID
   * @param {string} paid - 部品回答ID
   * @returns {Promise<Object>} APIレスポンスまたはエラー情報
   */
  async function init(mitsumoriNo, raid = null, paid = null) {
    log.debug(`START init mitsumoriNo: ${mitsumoriNo} raid: ${raid} paid: ${paid}`);
    setLoading(true);
    try {
      actions.showLoading();
      const result = await api.post({
        mode: "init",
        mitsumoriNo: mitsumoriNo,
        raid: raid,
        paid: paid,
      });

      log.debug("init result", result);
      if (result.result) {
        setRequestData(result.data);
        setQaList(result.qaList);
        setQaCnt(result.qaCnt || 0);
        setMikaitoList(result.mikaitoList);
        setIsAnswerOpen(result.isAnswerOpen);
      }
      return result;
    } catch (error) {
      log.error("init error", error);
      return { error: error.message, result: false };
    } finally {
      setLoading(false);
      actions.hideLoading();
    }
  }

  /**
   * 質問の選択処理
   * @param {Object} qa - 選択されたQ&Aデータ
   */
  function selectQa(qa) {
    log.debug("START selectQa", qa);
    setSelectedQa(qa);
  }

  /**
   * 質問の確認処理
   * @param {Object} confirmData - 入力内容データ
   * @returns {Promise<Object>} 確認結果
   */
  async function confirm() {
    log.debug("START confirm");

    try {
      actions.showLoading();
      const confirmData = {
        MitsumoriNo: initState.mitsumoriNo,
        QAnaiyo: form.QAnaiyo,
      };
      const result = await api.post({
        mode: "confirm",
        ...confirmData,
      });

      if (result.result) {
        setIsShowInit(false);
        setIsAnswerOpen(false);
        setIsShowConfirm(true);
        setIsShowFinish(false);
      }

      return result;
    } catch (error) {
      log.error("confirm error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * 質問の送信処理
   * @param {Object} questionData - 質問データ
   * @returns {Promise<Object>} 送信結果
   */
  async function submitQuestion() {
    log.debug("START submitQuestion form:", form);
    log.debug("START submitQuestion requestData:", requestData);

    try {
      actions.showLoading();
      const questionData = {
        MitsumoriNo: initState.mitsumoriNo,
        RAID: initState.raid,
        PAID: initState.paid,
        QAnaiyo: form.QAnaiyo,
        CorpID: requestData.CorpID,
        PartsName: requestData.PartsName,
      };
      const result = await api.post({
        mode: "submitQuestion",
        ...questionData,
      });

      log.debug("submitQuestion result", result);
      if (result.result) {
        // 成功時の処理
        if (result.action === "qa_ab_finish") {
          // 質問完了後の処理
          await init(questionData.MitsumoriNo, questionData.RAID, questionData.PAID);
        }
      }

      return result;
    } catch (error) {
      log.error("submitQuestion error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * 回答の送信処理
   * @returns {Promise<Object>} { result, action, error? }
   */
  async function submitAnswer() {
    log.debug("START submitAnswer", form);
    log.debug("START selectedQa", selectedQa);

    try {
      actions.showLoading();
      const answerData = {
        MitsumoriNo: selectedQa?.MitsumoriNo || initState.mitsumoriNo,
        RAID: selectedQa?.RAID || initState.raid,
        PAID: selectedQa?.PAID || initState.paid,
        Q_no: selectedQa?.Q_no || 1,
        QAnaiyo: form.QAnaiyo,
        CorpID: selectedQa?.CorpID || requestData?.CorpID,
        PartsName: requestData?.PartsName || "",
      };
      log.debug("submitAnswer answerData", answerData);
      const result = await api.post({
        mode: "submitAnswer",
        ...answerData,
      });

      log.debug("submitAnswer result", result);

      if (result.result) {
        // 成功時の処理
        if (result.action === "qa_ab_finish") {
          // 回答完了後の処理
          await init(answerData.MitsumoriNo, answerData.RAID, answerData.PAID);
        }
      }

      return result;
    } catch (error) {
      log.error("submitAnswer error", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * ダイアログを開く処理
   * @param {Object} dialogParams - ダイアログパラメータ
   */
  const openQaabDialog = (dialogParams = {}) => {
    const mergedParams = {
      mitsumoriNo: "",
      corpId: "",
      requestData: {},
      qa: { QAnaiyo: "", CorpID: "", MitsumoriNo: "", RAID: "", PAID: "" },
      qaList: [],
      partsKindName: {},
      error: null,
      onConfirm: null,
      onEdit: null,
      ...dialogParams,
    };
    log.debug("openQaabDialog params", mergedParams);
    open(mergedParams);
  };

  async function sendQaMail(info = {}) {
    const { to, subject, text } = info;
    log.debug("START sendQaMail", info);
    try {
      const res = await api.post({
        mode: "sendQaMail",
        to,
        subject,
        text,
      });
      return res;
    } catch (e) {
      log.error("sendQaMail error", e);
      return { error: e.message, result: false };
    } finally {
    }
  }

  // フォームデータとプロパティを返す
  return [
    {
      form,
      requestData,
      qaList,
      qaCnt,
      mikaitoList,
      loading,
      isShowInit,
      isAnswerOpen,
      isShowConfirm,
      isShowFinish,
      isShowMikaitoList,
      selectedQa,
    },
    {
      ...formProps,
      init,
      selectQa,
      submitQuestion,
      confirm,
      submitAnswer,
      open: openQaabDialog,
      onClose,
      setRequestData,
      renderDialog,
      sendQaMail,
      setIsShowInit,
      setIsShowMikaitoList,
      setIsAnswerOpen,
      setIsShowConfirm,
      setIsShowFinish,
    },
  ];
};

export default useQaabDialog;
